import {
  Box,
  Flex,
  Icon,
  Input,
  Select,
  Skeleton,
  Stack,
  Text,
  useTheme,
} from "@chakra-ui/react";
import React from "react";
import { Theme } from "../../theme";
import getAxios from "../../hooks/useAxios";
import Nodata from "../../components/Nodata";
import ParcelCard from "../../components/ParcelCard";
import { MdArrowBackIosNew, MdArrowForwardIos } from "react-icons/md";
import Cookies from "js-cookie";

const ParcelHistoryLifeTime = () => {
  const theme = useTheme<Theme>();
  const axios = getAxios();
  const access_token = Cookies.get("x-admin-auth");

  const [parcelHistory, setParcelHistory] = React.useState<any[]>([]);
  const [isDataFetching, setIsDataFetching] = React.useState(true);

  const [pageNumber, setPageNumber] = React.useState(() => {
    return Number(localStorage.getItem("parcelhistorypage")) || 1;
  });

  const [totalPages, setTotalPages] = React.useState<number>(0);
  const [total, setTotal] = React.useState<number>(0);
  const [nextNotAllowed, setNextNotAllowed] = React.useState(false);

  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const [status, setStatus] = React.useState("");

  const formatToISO8601 = (dateString: string) => {
    if (!dateString) return "";

    return new Date(`${dateString}T00:00:00`).toISOString();
  };

  const buildParcelHistoryUrl = () => {
    let url = `/parcels/all?page=${pageNumber}`;

    if (status && status !== "all") {
      url += `&status=${status}`;
    }

    if (startDate && endDate) {
      url += `&start_date=${formatToISO8601(startDate)}`;
      url += `&end_date=${formatToISO8601(endDate)}`;
    }

    return url;
  };

  const getParcelHistory = async () => {
    setIsDataFetching(true);

    try {
      const res = await axios.get(buildParcelHistoryUrl(), {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });

      console.log("parcel history", res.data);

      setParcelHistory(res.data.data || []);

      setTotalPages(res.data.pagination?.totalPages || 0);
      setTotal(res.data.pagination?.total || 0);

      setNextNotAllowed(
        pageNumber >= (res.data.pagination?.totalPages || 1) ||
          (res.data.data || []).length < 20,
      );
    } catch (error) {
      console.log("error parcel history", error);
      setParcelHistory([]);
    } finally {
      setIsDataFetching(false);
    }
  };

  React.useEffect(() => {
    getParcelHistory();
  }, [pageNumber, status, startDate, endDate]);

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        padding: "0 1rem",
      }}
    >
      {/* Header */}
      <Flex
        sx={{
          margin: "2.5rem 0 2rem 2rem",
          gap: "1rem",
          alignItems: "baseline",
        }}
      >
        <Text
          sx={{
            fontSize: theme.fontSizes.subHeading1,
          }}
        >
          Parcel Activities and History
        </Text>

        <Flex
          sx={{
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <Text
            sx={{
              fontSize: theme.fontSizes.body,
              marginRight: "0.25rem",
            }}
          >
            Total:
          </Text>

          <Skeleton isLoaded={!isDataFetching}>
            <Text
              sx={{
                fontSize: theme.fontSizes.body,
                color: "green",
              }}
            >
              {total}
            </Text>
          </Skeleton>
        </Flex>
      </Flex>

      {/* Filters */}
      <Box
        sx={{
          paddingX: "2rem",
        }}
      >
        <Box
          sx={{
            maxWidth: "40rem",
          }}
        >
          <Stack
            direction={{
              base: "column",
              md: "row",
            }}
            spacing={4}
            mb={6}
          >
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />

            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />

            <Select
              placeholder="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="all">All</option>
              <option value="delivered">Delivered</option>
              <option value="canceled">Canceled</option>
              <option value="picked-up">Picked Up</option>
              <option value="accepted">Accepted</option>
              <option value="searching">Searching</option>
              <option value="pending">Pending</option>
            </Select>
          </Stack>
        </Box>

        {/* Parcel list */}
        <Skeleton isLoaded={!isDataFetching} minHeight="20rem">
          {parcelHistory.length > 0 ? (
            parcelHistory.map((item, index) => (
              <ParcelCard
                key={item.id || item.parcel_id || index}
                item={item}
              />
            ))
          ) : (
            <Box
              sx={{
                padding: "2rem",
              }}
            >
              <Nodata heading="No Parcel History Listed" />
            </Box>
          )}
        </Skeleton>
      </Box>

      {/* Pagination */}
      <Box
        sx={{
          marginTop: theme.space.xl,
        }}
      >
        <Flex
          sx={{
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "3rem",
          }}
        >
          {/* Previous */}
          <Box>
            <Flex
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Flex
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "50%",
                  width: "2rem",
                  height: "2rem",
                }}
              >
                <Icon
                  as={MdArrowBackIosNew}
                  boxSize={5}
                  color={theme.colors.primary.main}
                  cursor={pageNumber !== 1 ? "pointer" : "not-allowed"}
                  onClick={() => {
                    if (pageNumber !== 1) {
                      const newPage = pageNumber - 1;

                      setPageNumber(newPage);
                      localStorage.setItem("parcelhistorypage", `${newPage}`);
                    }
                  }}
                />
              </Flex>

              <Text variant="caption">Previous Page</Text>
            </Flex>
          </Box>

          {/* Current page */}
          <Skeleton
            isLoaded={!isDataFetching}
            sx={{
              width: "2rem",
            }}
          >
            <Text variant="body">{pageNumber}</Text>
          </Skeleton>

          {/* Next */}
          <Box>
            <Flex
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Text variant="caption">Next Page</Text>

              <Flex
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: "50%",
                  width: "2rem",
                  height: "2rem",
                }}
              >
                <Icon
                  as={MdArrowForwardIos}
                  boxSize={5}
                  color={theme.colors.primary.main}
                  cursor={nextNotAllowed ? "not-allowed" : "pointer"}
                  onClick={() => {
                    if (!nextNotAllowed) {
                      const newPage = pageNumber + 1;

                      setPageNumber(newPage);
                      localStorage.setItem("parcelhistorypage", `${newPage}`);
                    }
                  }}
                />
              </Flex>
            </Flex>
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default ParcelHistoryLifeTime;
